import React, { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Typography,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    TextField,
    Alert,
    CircularProgress,
    Tooltip,
    IconButton,
    Chip,
    Divider,
    Grid,
} from "@mui/material";
import { Add, Edit, Delete, Straighten, Save, Cancel } from "@mui/icons-material";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import {
    createLoopSegmentAction,
    updateLoopSegmentAction,
    deleteLoopSegmentAction,
} from "../../../../store/actions/loopActions";
import { prettifyNumber } from "../../../../functions/Formatters";
import { updateLoopLengthAction } from "../../ProjectActions";
import FormattedNumberInput from "../../../CustomComponents/InputFields/FormattedNumberInput";

const validationSchema = Yup.object({
    name: Yup.string().required("Naam is verplicht").max(255, "Naam mag maximaal 255 karakters bevatten"),
    description: Yup.string().max(500, "Beschrijving mag maximaal 500 karakters bevatten"),
    length: Yup.number().nullable().min(0, "Lengte moet positief zijn"),
});

export default function LoopSegmentSelector({ loopId, editable = true, payload }) {
    const dispatch = useDispatch();
    const { loopSegments, loading, length: loopLength } = useSelector((state) => state.loop);
    const { selectedProject } = useSelector((state) => state.projects);
    
    const [editingSegment, setEditingSegment] = useState(null);
    const [isAdding, setIsAdding] = useState(false);
    const [localLength, setLocalLength] = useState(loopLength);

    // Calculate total length of all segments
    const combinedLength = useMemo(() => {
        return loopSegments
            .filter(segment => segment.length !== null && segment.length !== undefined)
            .reduce((sum, segment) => sum + segment.length, 0);
    }, [loopSegments]);

    // No useEffect needed - data is loaded by parent component

    const handleAddSegment = () => {
        setIsAdding(true);
        setEditingSegment(null);
    };

    const handleEditSegment = (segment) => {
        setEditingSegment(segment);
        setIsAdding(false);
    };

    const handleCancel = () => {
        setEditingSegment(null);
        setIsAdding(false);
    };

    const handleDeleteSegment = async (segmentId) => {
        if (window.confirm("Weet je zeker dat je dit segment wilt verwijderen?")) {
            await dispatch(deleteLoopSegmentAction(segmentId));
        }
    };

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            const segmentData = {
                loop: loopId,
                name: values.name,
                description: values.description || "",
                length: values.length || null,
            };

            if (editingSegment) {
                await dispatch(updateLoopSegmentAction(editingSegment.id, segmentData));
            } else {
                await dispatch(createLoopSegmentAction(segmentData));
            }

            handleCancel();
            resetForm();
        } catch (error) {
            console.error("Error saving segment:", error);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" component="h2">
                    Dijkvakken
                </Typography>
                {editable && !isAdding && (
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleAddSegment}
                    >
                        Dijkvak Toevoegen
                    </Button>
                )}
            </Box>

            {loopSegments.length === 0 && !isAdding ? (
                <Alert severity="info">
                    Geen dijkvakken gedefinieerd voor deze ontwerpronde. Voeg dijkvakken toe om te beginnen.
                </Alert>
            ) : (
                <Box>
                    <Box mb={2} p={2} bgcolor="grey.50" borderRadius={1} component={Grid} container spacing={2}>
                        <Grid item xs={12} sm={4}>
                        <Typography variant="subtitle1" fontWeight="bold" style={{ lineBreak: "no-break" }} >Totale lengte van project:</Typography>
                        </Grid>
                        <Grid item xs={12} sm={8}>
                        <TextField
                            value={localLength || ""}
                            onChange={(e) => setLocalLength(e.target.value)}
                            disabled={!editable}
                            size="small"
                            label="Lengte (m)"
                            fullWidth
                            placeholder={"Lengte (m)"}
                            InputProps={{
                                inputComponent: FormattedNumberInput,
                                inputProps: {
                                    name: `totalLength`,
                                    unit: "m",
                                },
                            }}
                            onBlur={() => updateLoopLengthAction(selectedProject.id, loopId, localLength, dispatch)}
                        />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                Lengte van de dijkvakken:
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <Typography>
                                {prettifyNumber(combinedLength, "m", false)}
                            </Typography>
                        </Grid>
                    </Box>

                        {localLength != combinedLength && !isNaN(localLength) && (
                            <Alert severity="warning">
                                De totale lengte van de dijkvakken is <b>{prettifyNumber(Math.abs(localLength - combinedLength), "m", false)} {combinedLength > localLength ? "groter" : "kleiner"} </b> dan de ingevulde lengte! Controleer de input.
                            </Alert>
                        )}
                    
                    {/* Segments List */}
                    <List>
                        {loopSegments.map((segment, index) => (
                            <React.Fragment key={segment.id}>
                                {editingSegment?.id === segment.id ? (
                                    // Edit Form
                                    <Formik
                                        initialValues={{
                                            name: editingSegment.name,
                                            description: editingSegment.description || "",
                                            length: editingSegment.length || "",
                                        }}
                                        validationSchema={validationSchema}
                                        onSubmit={handleSubmit}
                                    >
                                        {({ isSubmitting, values, setFieldValue }) => (
                                            <Form>
                                                <ListItem>
                                                    <Box sx={{ width: '100%' }}>
                                                        <Grid container spacing={2} alignItems="center">
                                                            <Grid item xs={12} sm={3}>
                                                                <TextField
                                                                    name="name"
                                                                    label="Naam"
                                                                    value={values.name}
                                                                    onChange={(e) => setFieldValue('name', e.target.value)}
                                                                    fullWidth
                                                                    size="small"
                                                                    required
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12} sm={4}>
                                                                <TextField
                                                                    name="description"
                                                                    label="Beschrijving"
                                                                    value={values.description}
                                                                    onChange={(e) => setFieldValue('description', e.target.value)}
                                                                    fullWidth
                                                                    size="small"
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12} sm={3}>
                                                                <TextField
                                                                    name="length"
                                                                    label="Lengte (m)"
                                                                    type="number"
                                                                    value={values.length}
                                                                    onChange={(e) => setFieldValue('length', e.target.value)}
                                                                    fullWidth
                                                                    size="small"
                                                                    inputProps={{ min: 0, step: 0.1 }}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12} sm={2}>
                                                                <Box display="flex" justifyContent="flex-end" gap={1}>
                                                                    <IconButton
                                                                        type="submit"
                                                                        color="primary"
                                                                        disabled={isSubmitting}
                                                                        size="small"
                                                                    >
                                                                        <Save />
                                                                    </IconButton>
                                                                    <IconButton
                                                                        onClick={handleCancel}
                                                                        size="small"
                                                                    >
                                                                        <Cancel />
                                                                    </IconButton>
                                                                </Box>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </ListItem>
                                            </Form>
                                        )}
                                    </Formik>
                                ) : (
                                    // Display Segment
                                    <ListItem>
                                        <ListItemText
                                            primary={
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Typography variant="subtitle1" fontWeight="medium">
                                                        {segment.name}
                                                    </Typography>
                                                    {segment.length !== null && (
                                                        <Chip
                                                            icon={<Straighten />}
                                                            label={prettifyNumber(segment.length, "m", true)}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Box>
                                            }
                                            secondary={
                                                segment.description && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        {segment.description}
                                                    </Typography>
                                                )
                                            }
                                        />
                                        {editable && (
                                            <ListItemSecondaryAction>
                                                <Tooltip title="Bewerken">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleEditSegment(segment)}
                                                    >
                                                        <Edit />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Verwijderen">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeleteSegment(segment.id)}
                                                    >
                                                        <Delete />
                                                    </IconButton>
                                                </Tooltip>
                                            </ListItemSecondaryAction>
                                        )}
                                    </ListItem>
                                )}
                                {index < loopSegments.length - 1 && <Divider />}
                            </React.Fragment>
                        ))}
                        
                        {/* Add Segment Form */}
                        {isAdding && (
                            <React.Fragment>
                                {loopSegments.length > 0 && <Divider />}
                                <Formik
                                    initialValues={{
                                        name: "",
                                        description: "",
                                        length: "",
                                    }}
                                    validationSchema={validationSchema}
                                    onSubmit={handleSubmit}
                                >
                                    {({ isSubmitting, values, setFieldValue }) => (
                                        <Form>
                                            <ListItem>
                                                <Box sx={{ width: '100%' }}>
                                                    <Grid container spacing={2} alignItems="center">
                                                        <Grid item xs={12} sm={3}>
                                                            <TextField
                                                                name="name"
                                                                label="Naam"
                                                                value={values.name}
                                                                onChange={(e) => setFieldValue('name', e.target.value)}
                                                                fullWidth
                                                                size="small"
                                                                required
                                                                autoFocus
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={4}>
                                                            <TextField
                                                                name="description"
                                                                label="Beschrijving"
                                                                value={values.description}
                                                                onChange={(e) => setFieldValue('description', e.target.value)}
                                                                fullWidth
                                                                size="small"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={3}>
                                                            <TextField
                                                                name="length"
                                                                label="Lengte (m)"
                                                                type="number"
                                                                value={values.length}
                                                                onChange={(e) => setFieldValue('length', e.target.value)}
                                                                fullWidth
                                                                size="small"
                                                                inputProps={{ min: 0, step: 0.1 }}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={2}>
                                                            <Box display="flex" justifyContent="flex-end" gap={1}>
                                                                <IconButton
                                                                    type="submit"
                                                                    color="primary"
                                                                    disabled={isSubmitting}
                                                                    size="small"
                                                                >
                                                                    <Save />
                                                                </IconButton>
                                                                <IconButton
                                                                    onClick={handleCancel}
                                                                    size="small"
                                                                >
                                                                    <Cancel />
                                                                </IconButton>
                                                            </Box>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </ListItem>
                                        </Form>
                                    )}
                                </Formik>
                            </React.Fragment>
                        )}
                        
                        {/* Add Segment List Item */}
                        {editable && !isAdding && (
                            <React.Fragment>
                                {loopSegments.length > 0 && <Divider />}
                                <ListItem 
                                    button 
                                    onClick={handleAddSegment}
                                    sx={{ 
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: 'action.hover'
                                        }
                                    }}
                                >
                                    <ListItemText
                                        primary={
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Add fontSize="small" color="action" />
                                                <Typography variant="subtitle1" color="text.secondary">
                                                    Dijkvak toevoegen
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                </ListItem>
                            </React.Fragment>
                        )}
                    </List>
                </Box>
            )}
        </Box>
    );
} 